import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { ApiService } from 'services/api.service';
import { IrLogInfoModalComponent } from 'components/modal/ir-log-info-modal/ir-log-info-modal.component';
import { IrRequestInfoModalComponent } from 'components/modal/ir-request-info-modal/ir-request-info-modal.component';
import { IrSendEmailModalComponent } from 'components/modal/ir-send-email-modal/ir-send-email-modal.component';
import { formatDatetime } from 'util/date.utility';
import { MAX_LENGTH_NAME } from 'util/form.utility';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import {
  FILE_OBJECT_TYPE_INFO_REQUEST_COMMUNICATION_BACKOFFICE,
  FILE_OBJECT_TYPE_INFO_REQUEST_COMMUNICATION_FRONTOFFICE
} from 'types/information-request.interface';
import type {
  BackofficeInformationRequestCard,
  BackofficeInformationRequestCommunicationListItem,
  BackofficeInformationRequestCommunicationCard,
  InformationRequestBackofficeStatus,
  InformationRequestCard
} from 'types/information-request.interface';

const REQUEST_INFO_ALLOWED_STATUSES: InformationRequestBackofficeStatus[] = [
  'PreliminaryResearch',
  'InProcessing',
  'AdditionalInfoRequested',
  'AdditionalInfoSubmitted'
];

@Component({
  selector: 'app-ir-conversation',
  standalone: true,
  imports: [
    TranslateModule,
    NgClass,
    TooltipComponent,
    IrLogInfoModalComponent,
    IrRequestInfoModalComponent,
    IrSendEmailModalComponent,
    FileUploadComponent
  ],
  templateUrl: './ir-conversation.component.html',
  styleUrls: ['./ir-conversation.component.scss', '../../form/base-form.scss']
})
export class IrConversationComponent implements OnInit, OnChanges {
  public readonly maxFieldLength = MAX_LENGTH_NAME;
  public readonly formatDatetime = formatDatetime;
  @Input() informationRequest!: BackofficeInformationRequestCard;
  @Input() conversationList!: BackofficeInformationRequestCommunicationListItem[];
  @Output() markedAsRead: EventEmitter<void> = new EventEmitter();
  @Output() messageSaved: EventEmitter<BackofficeInformationRequestCommunicationListItem | BackofficeInformationRequestCommunicationCard> =
    new EventEmitter();
  @Output() requestRefreshed: EventEmitter<InformationRequestCard> = new EventEmitter();
  isAddClientNoteModalOpen: boolean = false;
  isRequestAdditionalInfoModalOpen: boolean = false;
  isSendEmailModalOpen: boolean = false;
  isEditClientNoteModalOpen: boolean = false;
  editNote: BackofficeInformationRequestCommunicationListItem | BackofficeInformationRequestCommunicationCard | null = null;
  public readonly communicationFileTypeFrontoffice = FILE_OBJECT_TYPE_INFO_REQUEST_COMMUNICATION_FRONTOFFICE;
  public readonly communicationFileTypeBackoffice = FILE_OBJECT_TYPE_INFO_REQUEST_COMMUNICATION_BACKOFFICE;

  private currentStatus: InformationRequestBackofficeStatus = 'Draft';

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.currentStatus = this.informationRequest.backofficeStatus;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['informationRequest']) {
      this.informationRequest = changes['informationRequest'].currentValue;
      this.currentStatus = this.informationRequest.backofficeStatus;
    }
  }

  get shouldRenderRequestAdditionalInfoButton(): boolean {
    return REQUEST_INFO_ALLOWED_STATUSES.includes(this.currentStatus);
  }

  requestAdditionalInfo(): void {
    this.isRequestAdditionalInfoModalOpen = true;
  }

  sendEmailMessage(): void {
    this.isSendEmailModalOpen = true;
  }

  addClientNote(): void {
    this.isAddClientNoteModalOpen = true;
  }

  onMessageSaved(message: BackofficeInformationRequestCommunicationListItem | BackofficeInformationRequestCommunicationCard): void {
    this.messageSaved.emit(message);
  }

  onRequestRefreshed(data: InformationRequestCard): void {
    this.requestRefreshed.emit(data);
  }

  markAsRead(id: string): void {
    this.apiService.markIrCommunicationAsRead(id).subscribe(() => {
      this.conversationList = this.conversationList.map((item) => {
        if (item.id === id && item.frontofficeText) {
          item.isReadByBackoffice = true;
        }

        this.markedAsRead.emit();
        return item;
      });
    });
  }

  editMessage(message: BackofficeInformationRequestCommunicationListItem): void {
    this.editNote = message;
    this.isEditClientNoteModalOpen = true;
  }
}
